import React from "react";
import classNames from "classnames";

export interface FeatureHighlight {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface FeatureHighlightCardProps extends FeatureHighlight {
  iconHeight?: string;
  titleClassName?: string;
  descriptionClassName?: string;
}

export const FeatureHighlightCard = ({
  icon,
  title,
  description,
  iconHeight = "h-[100px] md:h-[120px]",
  titleClassName = "text-[20px] md:text-[24px] font-bold text-center",
  descriptionClassName = "text-[13px] md:text-[14px] text-lavender-50 text-center",
}: FeatureHighlightCardProps) => {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className={classNames("flex items-end justify-center", iconHeight)}>{icon}</div>
      <h3 className={titleClassName}>{title}</h3>
      <p className={descriptionClassName}>{description}</p>
    </div>
  );
};

interface FeatureHighlightsBoxProps {
  featureHighlights: FeatureHighlight[];
  className?: string;
  innerClassName?: string;
  gridClassName?: string;
  cardIconHeight?: string;
  cardTitleClassName?: string;
  cardDescriptionClassName?: string;
}

export const FeatureHighlightsBox = React.forwardRef<HTMLDivElement, FeatureHighlightsBoxProps>(
  (
    {
      featureHighlights,
      className = "",
      innerClassName = "px-4 md:px-6 pt-[30px] md:pt-[50px] pb-8 md:pb-12",
      gridClassName = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-x-[68px] lg:gap-y-3",
      cardIconHeight,
      cardTitleClassName,
      cardDescriptionClassName,
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={classNames("rounded-[6px] p-[1px] bg-gradient-to-r from-[#2C2B4C] via-[#F4C874] to-[#2C2B4C]", className)}
      >
        <div className={classNames("relative rounded-[6px] bg-purple-500", innerClassName)}>
          <div
            className="absolute inset-0 rounded-[6px]"
            style={{
              background: "radial-gradient(50% 100% at 50% 0%, rgba(244, 200, 116, 0.10) 0%, rgba(244, 200, 116, 0.00) 100%)",
            }}
          />
          <div className={classNames("relative grid", gridClassName)}>
            {featureHighlights.map((feature, index) => (
              <FeatureHighlightCard
                key={index}
                {...feature}
                iconHeight={cardIconHeight}
                titleClassName={cardTitleClassName}
                descriptionClassName={cardDescriptionClassName}
              />
            ))}
          </div>
        </div>
      </div>
    );
  },
);

export const PLUS_FEATURE_ICONS = {
  adFree: "https://static.bigbrain.gg/assets/ugg/icons/ugg-plus-no-ads.png",
  profiles: "https://static.bigbrain.gg/assets/ugg/icons/ugg-plus-profiles.png",
  sharedSocials: "https://static.bigbrain.gg/assets/ugg/icons/ugg-plus-shared-socials.png",
  support: "https://static.bigbrain.gg/assets/ugg/icons/ugg-plus-support.png",
};

export const PLUS_FEATURE_DATA = {
  adFree: {
    title: "Ad-Free Experience",
    description: "Enjoy a stunning ad-free experience across our entire website and app with U.GG PLUS.",
  },
  profiles: {
    title: "Profiles PLUS",
    description: "Your LoL profile will stand out amongst the rest with a golden flair and added custom features.",
  },
  sharedSocials: {
    title: "Shared Socials",
    description: "Gain followers on your favorite socials like Twitch or Discord with links right on your profile.",
  },
  support: {
    title: "Priority Support",
    description: "PLUS members benefit from priority user support.",
  },
};
